import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  InputAdornment,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";
import { Filter, Shield, CircleDot } from "lucide-react";

import LayerdIcon from "../../../common/LayerdIcon";
import RefreshIconButton from "../../../common/RefreshIconButton";
import colors from "../../../../utils/colors";
import { SafeTextField } from "../../../common/Greens";
import { filterInputSx } from "../services/api_security/asecUi";

const brandBlue = colors.primary[300];

const EVENT_FILTER_BAR_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: 2,
  backgroundColor: "white",
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
};

/** Display options only — "all" is empty state (cleared), matching Bot Event pills. */
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

const ENFORCEMENT_OPTIONS = [
  { value: "Enforced", label: "Enforced" },
  { value: "Detection", label: "Detection" },
  { value: "Unhealthy", label: "Unhealthy" },
  { value: "Disabled", label: "Disabled" },
];

function SitesFilterDropdown({ label, icon, value, options, onChange, onClear }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selectedLabel = options.find((o) => o.value === value)?.label || "";
  const hasValue = Boolean(value && value !== "all");

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          height: 36,
          borderRadius: "8px",
          border: `1px solid ${hasValue ? brandBlue : colors.neutral[200]}`,
          backgroundColor: hasValue ? "rgba(99, 113, 247, 0.06)" : "#fff",
          cursor: "pointer",
          minWidth: 140,
          transition: "border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease, transform 120ms ease",
          "&:hover": {
            borderColor: brandBlue,
            backgroundColor: "rgba(99, 113, 247, 0.08)",
            boxShadow: "0 1px 4px rgba(99, 113, 247, 0.12)",
            "& .filter-label": {
              color: brandBlue,
            },
          },
          "&:active": {
            transform: "scale(0.98)",
          },
        }}
      >
        <LayerdIcon icon={icon} size={16} color="primary" />
        <Typography
          className="filter-label"
          sx={{
            fontSize: 14,
            color: hasValue ? brandBlue : colors.neutral[800],
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hasValue ? selectedLabel : label}
        </Typography>
        {hasValue ? (
          <CloseIcon
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            sx={{ fontSize: 14, color: brandBlue, "&:hover": { color: colors.red[100] } }}
          />
        ) : (
          <ArrowDownIcon sx={{ fontSize: 16, color: brandBlue }} />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.neutral[200]}`,
            width: 240,
            maxHeight: 320,
            backgroundColor: "#fff",
          },
        }}
      >
        <Box sx={{ maxHeight: 200, overflow: "auto", backgroundColor: "#fff", pt: 0.5 }}>
          <List disablePadding dense>
            {options.map((opt) => (
              <ListItem key={opt.value} disablePadding>
                <ListItemButton
                  onClick={() => {
                    onChange(opt.value);
                    setAnchorEl(null);
                  }}
                  selected={value === opt.value}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    "&:hover": { backgroundColor: "#f0f4ff" },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(99, 113, 247, 0.08)",
                      "&:hover": { backgroundColor: "rgba(99, 113, 247, 0.12)" },
                    },
                  }}
                >
                  <ListItemText
                    primary={opt.label}
                    primaryTypographyProps={{
                      sx: { fontSize: 14, color: colors.neutral[800] },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  );
}

SitesFilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default function SitesFilterBar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusChange,
  enforcementFilter,
  onEnforcementChange,
  hasActiveFilters,
  onClearAll,
  onRefresh,
  isRefreshing,
}) {
  return (
    <Box sx={EVENT_FILTER_BAR_SX}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ gap: 1.5, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={Filter} size={18} color="primary" />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
              Filters
            </Typography>
          </Stack>

          <SafeTextField
            placeholder="Search domain, origin, or project…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              ...filterInputSx,
              minWidth: 200,
              maxWidth: 320,
              flex: "1 1 200px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: colors.neutral[500] }} />
                </InputAdornment>
              ),
            }}
          />

          <SitesFilterDropdown
            label="Status"
            icon={CircleDot}
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={onStatusChange}
            onClear={() => onStatusChange("all")}
          />

          <SitesFilterDropdown
            label="Enforcement"
            icon={Shield}
            value={enforcementFilter}
            options={ENFORCEMENT_OPTIONS}
            onChange={onEnforcementChange}
            onClear={() => onEnforcementChange("all")}
          />

          {hasActiveFilters && (
            <Chip
              label="Clear All"
              size="small"
              onClick={onClearAll}
              sx={{
                height: 36,
                fontSize: 14,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: `1px solid ${colors.neutral[200]}`,
                color: colors.neutral[800],
                "&:hover": {
                  borderColor: colors.red[100],
                  color: colors.red[100],
                },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
          <RefreshIconButton onClick={onRefresh} disabled={isRefreshing} loading={isRefreshing} />
        </Stack>
      </Stack>
    </Box>
  );
}

SitesFilterBar.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  statusFilter: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  enforcementFilter: PropTypes.string.isRequired,
  onEnforcementChange: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
  onClearAll: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool,
};
